import { useEffect, useState } from 'react';
import { api, ApiError, type HostsPayload, type RealInventoryPayload } from '../api';
import { useStore } from '../state/store';
import { Badge, Button, Card, CardBody, EmptyState, StatusDot } from '../design/primitives';

/**
 * Inventory the product actually found, shown next to — never merged into — the
 * simulated estate below.
 *
 * When the read is unavailable this says so plainly instead of rendering
 * plausible-looking rows. The whole point of the section is that a viewer can
 * trust these entries are real, which only holds if it stays empty when it
 * cannot prove otherwise.
 */
function RealInventorySection({ data }: { data: RealInventoryPayload }) {
  const empty = data.machines.length === 0 && data.agents.length === 0 && data.tools.length === 0;

  return (
    <section>
      <h2 className="mb-section-title">
        Discovered on real machines
        <Badge tone="allow">Not simulated</Badge>
      </h2>
      <Card>
        <CardBody>
          <p className="mb-lede">
            Everything above is a staged bank. This section is the opposite: agents, tools and machines the
            FullCourtDefense agent genuinely found on real hardware in this organisation, read back from the same
            inventory a customer sees in their console.
          </p>

          {data.status !== 'live' && <p className="mb-note">{data.reason}</p>}

          {data.status === 'live' && empty && (
            <p className="mb-note">
              No real inventory in this organisation yet. Run <code className="mb-mono">fullcourtdefense discover
              --surface all --upload</code> on a machine to populate it.
            </p>
          )}

          {data.status === 'live' && !empty && (
            <>
              <div className="mb-stat-row">
                <div className="mb-stat mb-stat--ok">
                  <div className="mb-stat__value">{data.machines.length}</div>
                  <div className="mb-stat__label">Real machines</div>
                </div>
                <div className="mb-stat mb-stat--ok">
                  <div className="mb-stat__value">{data.agents.length}</div>
                  <div className="mb-stat__label">Discovered agents</div>
                </div>
                <div className="mb-stat mb-stat--ok">
                  <div className="mb-stat__value">{data.tools.length}</div>
                  <div className="mb-stat__label">Discovered tools</div>
                </div>
              </div>

              {data.machines.length > 0 && (
                <table className="mb-table">
                  <thead>
                    <tr>
                      <th>Machine</th>
                      <th>Platform</th>
                      <th>Operator</th>
                      <th>Last heartbeat</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.machines.map(machine => (
                      <tr key={machine.machineId}>
                        <td>{machine.hostname || machine.machineId.slice(0, 16)}</td>
                        <td>{machine.platform || '—'}</td>
                        <td className="mb-mono">{machine.developerName || '—'}</td>
                        <td>
                          {machine.lastHeartbeatAt
                            ? new Date(machine.lastHeartbeatAt).toLocaleString('en-GB', { hour12: false })
                            : 'never'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}

              {data.agents.length > 0 && (
                <>
                  <h3 className="mb-section-title">Agents found</h3>
                  <table className="mb-table">
                    <thead>
                      <tr>
                        <th>Agent</th>
                        <th>Source</th>
                        <th>Tools</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.agents.slice(0, 25).map(agent => (
                        <tr key={agent.id}>
                          <td className="mb-mono">{agent.name}</td>
                          <td>{agent.source}</td>
                          <td>{agent.toolCount}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </>
              )}

              {data.tools.length > 0 && (
                <>
                  <h3 className="mb-section-title">Tools found</h3>
                  <table className="mb-table">
                    <thead>
                      <tr>
                        <th>Tool</th>
                        <th>Server</th>
                        <th>Risk</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.tools.slice(0, 25).map(tool => (
                        <tr key={tool.id}>
                          <td className="mb-mono">{tool.name}</td>
                          <td>{tool.serverName || tool.type}</td>
                          <td>
                            <Badge
                              tone={
                                tool.riskLevel === 'critical' || tool.riskLevel === 'high'
                                  ? 'block'
                                  : tool.riskLevel === 'medium'
                                    ? 'approval'
                                    : 'allow'
                              }
                            >
                              {tool.riskLevel || 'unrated'}
                            </Badge>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </>
              )}

              <p className="mb-note">
                {data.excludedSimulated.machines + data.excludedSimulated.agents + data.excludedSimulated.tools} record
                {data.excludedSimulated.machines + data.excludedSimulated.agents + data.excludedSimulated.tools === 1
                  ? ''
                  : 's'}{' '}
                belonging to the simulated Meridian estate were filtered out of this section.
              </p>
            </>
          )}
        </CardBody>
      </Card>
    </section>
  );
}

/**
 * The machine fleet.
 *
 * Each host here is genuinely enrolled in AgentGuard: it has its own credential,
 * sends heartbeats, uploads discovery, and answers remote actions. The remote
 * action round trip is the part worth demonstrating — an operator can queue an
 * action in the AgentGuard console and watch this page's machines answer it.
 */
export function Fleet() {
  const { bootstrap } = useStore();
  const [data, setData] = useState<HostsPayload>();
  const [real, setReal] = useState<RealInventoryPayload>();
  const [busy, setBusy] = useState<string>();
  const [result, setResult] = useState<{ hostId: string; summary: string; note?: string }>();
  const [error, setError] = useState<string>();

  useEffect(() => {
    const load = () =>
      api.hosts()
        .then(setData)
        .catch(caught => setError(caught.message));
    void load();
    const timer = window.setInterval(load, 15_000);
    return () => window.clearInterval(timer);
  }, []);

  // Read once per mount rather than on the fleet's 15s cadence: this is
  // discovery-scan data, and the server caches it anyway.
  useEffect(() => {
    api.realInventory().then(setReal).catch(() => setReal(undefined));
  }, []);

  const runAction = async (hostId: string, action: string) => {
    setBusy(`${hostId}:${action}`);
    setError(undefined);
    try {
      const outcome = await api.machineAction({ hostId, action });
      setResult({ hostId, summary: outcome.summary, note: outcome.note });
      setData(await api.hosts());
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'That action could not be run.');
    } finally {
      setBusy(undefined);
    }
  };

  if (error && !data) return <EmptyState>Could not load the fleet: {error}</EmptyState>;
  if (!data || !bootstrap) return null;

  const enrolledIds = new Set(data.fleet.enrolled.map(machine => machine.hostId));

  return (
    <div className="mb-page">
      <Card>
        <CardBody>
          <p className="mb-lede">
            Every machine in Meridian's estate is enrolled in AgentGuard with its own credential. They heartbeat, upload
            what they find locally, and answer actions queued from the console.
          </p>
          <div className="mb-stat-row">
            <div className="mb-stat">
              <div className="mb-stat__value">{data.fleet.expected}</div>
              <div className="mb-stat__label">Machines in the estate</div>
            </div>
            <div
              className={`mb-stat ${
                data.fleet.enrolled.length < data.fleet.expected ? 'mb-stat--warn' : 'mb-stat--ok'
              }`}
            >
              <div className="mb-stat__value">{data.fleet.enrolled.length}</div>
              <div className="mb-stat__label">Enrolled and reporting</div>
            </div>
          </div>
          {data.fleet.lastError && <p className="mb-note">{data.fleet.lastError}</p>}
        </CardBody>
      </Card>

      {result && (
        <Card>
          <CardBody>
            <h3 className="mb-section-title">{result.hostId} responded</h3>
            <p className="mb-section-sub" style={{ marginBottom: 0 }}>
              {result.summary}
            </p>
            {result.note && <p className="mb-note">{result.note}</p>}
          </CardBody>
        </Card>
      )}

      {error && (
        <p className="mb-op__error" role="alert">
          {error}
        </p>
      )}

      {real && <RealInventorySection data={real} />}

      <section>
        <h2 className="mb-section-title">
          Meridian estate
          <Badge tone="approval">Simulated</Badge>
        </h2>
        <div className="mb-fleet-grid">
          {data.hosts.map((host: any) => {
            const enrolled = enrolledIds.has(host.id);
            const machine = data.fleet.enrolled.find(candidate => candidate.hostId === host.id);
            return (
              <Card key={host.id}>
                <CardBody>
                  <div className="mb-fleet__head">
                    <div className="mb-grow">
                      <h3 className="mb-section-title" style={{ marginBottom: 0 }}>
                        {host.hostname}
                      </h3>
                      <div className="mb-fleet__meta">
                        {host.nickname} · {host.environment} · {host.owner}
                      </div>
                    </div>
                    <span className="mb-fleet__status">
                      <StatusDot
                        status={enrolled ? 'online' : 'offline'}
                        label={enrolled ? 'reporting' : 'not enrolled'}
                      />
                      {enrolled ? 'reporting' : 'not enrolled'}
                    </span>
                  </div>

                  <dl className="mb-fleet__kv">
                    <dt>Platform</dt>
                    <dd>{host.platform}</dd>
                    <dt>Address</dt>
                    <dd className="mb-mono">{host.ipAddress}</dd>
                    <dt>Machine id</dt>
                    <dd>
                      <code className="mb-mono">{host.machineId?.slice(0, 16)}…</code>
                    </dd>
                    <dt>Posture</dt>
                    <dd>
                      <Badge tone={host.postureScore >= 80 ? 'allow' : host.postureScore >= 60 ? 'approval' : 'block'}>
                        {host.postureScore}/100
                      </Badge>
                    </dd>
                    {machine?.lastHeartbeatAt && (
                      <>
                        <dt>Last heartbeat</dt>
                        <dd>{new Date(machine.lastHeartbeatAt).toLocaleTimeString('en-GB', { hour12: false })}</dd>
                      </>
                    )}
                  </dl>

                  <div className="mb-fleet__actions">
                    {['health_check', 'discovery_scan', 'collect_diagnostics'].map(action => (
                      <Button
                        key={action}
                        size="sm"
                        disabled={!enrolled || busy === `${host.id}:${action}`}
                        onClick={() => runAction(host.id, action)}
                      >
                        {busy === `${host.id}:${action}` ? 'Running…' : action.replace(/_/g, ' ')}
                      </Button>
                    ))}
                  </div>
                </CardBody>
              </Card>
            );
          })}
        </div>
      </section>
    </div>
  );
}
